import 'client-only';

import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import { get } from '@/utils/http/client';
import { urls } from '@/utils/urls';

interface ServerFlashMessage {
  type: 'success' | 'error' | 'info';
  content: string;
}

const flashMessageTypeStates = {
  error: 'critical',
  info: 'light',
  success: 'success',
} as const;

export const useServerFlashMessages = ({
  brand,
  createFlashMessageFromSearchParam = false,
}: {
  brand: Brand;
  createFlashMessageFromSearchParam?: boolean;
}) => {
  const setFlashMessages = useSetAtom(FlashMessagesState.flashMessagesAtom);

  const refresh = useCallback(() => {
    get<{ data: ServerFlashMessage[] }>(urls[brand].api.session.flashMessages)
      .then((response) => {
        const serverFlashMessages = response.data.data.map((flashMessage) => {
          return {
            children: flashMessage.content,
            state: flashMessageTypeStates[flashMessage.type],
          };
        });

        setFlashMessages((existing) => [...existing, ...serverFlashMessages]);
      })
      .catch(() => {
        // Catching to avoid test failures - we chose not to log here as these are likely network errors
      });
  }, [brand, setFlashMessages]);

  // Fetch flash messages on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Create flash message from search param
  useEffect(() => {
    if (!createFlashMessageFromSearchParam) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const errorMessage = params.get('error');
    if (errorMessage) {
      setFlashMessages((existing) => [
        ...existing,
        { children: decodeURIComponent(errorMessage), state: 'critical' },
      ]);
    }
  }, [createFlashMessageFromSearchParam, setFlashMessages]);

  return { refresh };
};
